import { SITE_BASE_URL } from '../constants';
import { urlJoin } from './utils';

type JsonLd = Record<string, unknown>;

export interface BusinessInfo {
  legalName?: string | null;
  telephone?: string | null;
  contactEmail?: string | null;
  streetAddress?: string | null;
  addressLocality?: string | null;
  addressRegion?: string | null;
  postalCode?: string | null;
  addressCountry?: string | null;
  latitude?: number | null;
  longitude?: number | null;
  openingHours?: (string | null)[] | null;
}

export interface SocialLinks {
  facebookUrl?: string | null;
  instagramUrl?: string | null;
  whatsappUrl?: string | null;
  linkedinUrl?: string | null;
}

export interface OrganizationSchemaInput {
  name: string;
  url: string;
  logoUrl?: string;
  description?: string;
  businessInfo?: BusinessInfo | null;
  social?: SocialLinks | null;
}

function pruneEmpty<T extends JsonLd>(obj: T): T {
  const result: JsonLd = {};
  for (const [key, value] of Object.entries(obj)) {
    if (value === undefined || value === null) continue;
    if (typeof value === 'string' && value.trim() === '') continue;
    if (Array.isArray(value) && value.length === 0) continue;
    result[key] = value;
  }
  return result as T;
}

export function buildOrganizationSchema({
  name,
  url,
  logoUrl,
  description,
  businessInfo,
  social,
}: OrganizationSchemaInput): JsonLd {
  // @type Restaurant inherits from Organization + LocalBusiness, so all
  // Organization-compatible consumers (Google knowledge panel, etc.) still
  // recognise it while adding food-specific signals.
  const restaurantFields = {
    servesCuisine: ['International', 'Portuguese'],
    priceRange: '€€€€',
    acceptsReservations: true,
  };

  const address = businessInfo
    ? pruneEmpty({
        '@type': 'PostalAddress',
        streetAddress: businessInfo.streetAddress,
        addressLocality: businessInfo.addressLocality,
        addressRegion: businessInfo.addressRegion,
        postalCode: businessInfo.postalCode,
        addressCountry: businessInfo.addressCountry,
      })
    : null;

  const geo =
    businessInfo?.latitude != null && businessInfo?.longitude != null
      ? {
          '@type': 'GeoCoordinates',
          latitude: businessInfo.latitude,
          longitude: businessInfo.longitude,
        }
      : null;

  const sameAs = social
    ? [social.facebookUrl, social.instagramUrl, social.linkedinUrl, social.whatsappUrl].filter(
        (u): u is string => !!u,
      )
    : [];

  const contactPoint = businessInfo?.contactEmail
    ? pruneEmpty({
        '@type': 'ContactPoint',
        contactType: 'customer support',
        email: businessInfo.contactEmail,
        telephone: businessInfo.telephone,
      })
    : null;

  return pruneEmpty({
    '@context': 'https://schema.org',
    '@type': 'Restaurant',
    '@id': urlJoin(url, '#organization'),
    ...restaurantFields,
    name,
    legalName: businessInfo?.legalName,
    url,
    logo: logoUrl,
    description,
    telephone: businessInfo?.telephone,
    email: businessInfo?.contactEmail,
    address: address && Object.keys(address).length > 1 ? address : undefined,
    geo,
    openingHours: businessInfo?.openingHours?.filter((h): h is string => !!h),
    sameAs: sameAs.length > 0 ? sameAs : undefined,
    contactPoint,
  });
}

export function buildWebSiteSchema({
  name,
  url,
  description,
}: {
  name: string;
  url: string;
  description?: string;
}): JsonLd {
  return pruneEmpty({
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    '@id': urlJoin(url, '#website'),
    name,
    url,
    description,
  });
}

function humanizeSlug(slug: string): string {
  return slug
    .split('-')
    .filter(Boolean)
    .map(w => w.charAt(0).toUpperCase() + w.slice(1))
    .join(' ');
}

export function buildBreadcrumbListSchema({
  pathname,
  currentPageTitle,
  homeLabel = 'Home',
}: {
  pathname: string;
  currentPageTitle?: string;
  homeLabel?: string;
}): JsonLd | null {
  const cleanPath = pathname.replace(/^\/+|\/+$/g, '');
  if (!cleanPath) return null;

  const segments = cleanPath.split('/').filter(Boolean);
  const items = [
    {
      '@type': 'ListItem',
      position: 1,
      name: homeLabel,
      item: urlJoin(SITE_BASE_URL, '/'),
    },
  ];

  segments.forEach((segment, index) => {
    const isLast = index === segments.length - 1;
    const itemPath = '/' + segments.slice(0, index + 1).join('/') + '/';
    items.push({
      '@type': 'ListItem',
      position: index + 2,
      name: isLast && currentPageTitle ? currentPageTitle : humanizeSlug(segment),
      item: urlJoin(SITE_BASE_URL, itemPath),
    });
  });

  return {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: items,
  };
}

export interface FAQItem {
  question: string;
  answer: string;
}

export function buildFAQPageSchema(items: FAQItem[]): JsonLd | null {
  const mainEntity = items
    .filter(item => item.question.trim() && item.answer.trim())
    .map(item => ({
      '@type': 'Question',
      name: item.question,
      acceptedAnswer: {
        '@type': 'Answer',
        text: item.answer,
      },
    }));
  if (mainEntity.length === 0) return null;
  return {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity,
  };
}

export interface MenuItemInput {
  name: string;
  description?: string;
  imageUrl?: string;
  wineInformation?: string;
}

export interface MenuSectionInput {
  name: string;
  items: MenuItemInput[];
}

export interface MenuSchemaInput {
  name: string;
  url: string;
  sections: MenuSectionInput[];
}

export function buildMenuSchema({ name, url, sections }: MenuSchemaInput): JsonLd | null {
  const nonEmpty = sections.filter(section => section.items.length > 0);
  if (nonEmpty.length === 0) return null;
  return {
    '@context': 'https://schema.org',
    '@type': 'Menu',
    name,
    url,
    hasMenuSection: nonEmpty.map(section => ({
      '@type': 'MenuSection',
      name: section.name,
      hasMenuItem: section.items.map(item =>
        pruneEmpty({
          '@type': 'MenuItem',
          name: item.name,
          description: item.description,
          image: item.imageUrl,
          suitableForDiet: undefined,
          menuAddOn: item.wineInformation
            ? [
                {
                  '@type': 'MenuItem',
                  name: item.wineInformation,
                },
              ]
            : undefined,
        }),
      ),
    })),
  };
}

export interface VideoObjectInput {
  name: string;
  description?: string;
  thumbnailUrl?: string;
  uploadDate?: string;
  embedUrl?: string;
  contentUrl?: string;
}

export function buildVideoObjectSchema(input: VideoObjectInput): JsonLd | null {
  if (!input.name || !input.uploadDate || !input.thumbnailUrl) return null;
  return pruneEmpty({
    '@context': 'https://schema.org',
    '@type': 'VideoObject',
    name: input.name,
    description: input.description,
    thumbnailUrl: input.thumbnailUrl,
    uploadDate: input.uploadDate,
    embedUrl: input.embedUrl,
    contentUrl: input.contentUrl,
  });
}

export interface EventOfferInput {
  url: string;
  price?: number;
  priceCurrency?: string;
  availability?: string;
  validFrom?: string;
}

export interface EventSchemaInput {
  name: string;
  description?: string;
  imageUrl?: string;
  url: string;
  startDate: string;
  endDate?: string;
  locationName?: string;
  businessInfo?: BusinessInfo | null;
  offers?: EventOfferInput[];
  organizerName?: string;
  organizerUrl?: string;
}

export function buildEventSchema({
  name,
  description,
  imageUrl,
  url,
  startDate,
  endDate,
  locationName,
  businessInfo,
  offers,
  organizerName,
  organizerUrl,
}: EventSchemaInput): JsonLd {
  const address = businessInfo
    ? pruneEmpty({
        '@type': 'PostalAddress',
        streetAddress: businessInfo.streetAddress,
        addressLocality: businessInfo.addressLocality,
        addressRegion: businessInfo.addressRegion,
        postalCode: businessInfo.postalCode,
        addressCountry: businessInfo.addressCountry,
      })
    : null;

  const location = pruneEmpty({
    '@type': 'Place',
    name: locationName,
    address: address && Object.keys(address).length > 1 ? address : undefined,
  });

  return pruneEmpty({
    '@context': 'https://schema.org',
    '@type': 'FoodEvent',
    name,
    description,
    image: imageUrl,
    url,
    startDate,
    endDate,
    eventAttendanceMode: 'https://schema.org/OfflineEventAttendanceMode',
    eventStatus: 'https://schema.org/EventScheduled',
    location: Object.keys(location).length > 1 ? location : undefined,
    organizer: organizerName
      ? pruneEmpty({
          '@type': 'Organization',
          name: organizerName,
          url: organizerUrl,
        })
      : undefined,
    offers: offers?.map(offer =>
      pruneEmpty({
        '@type': 'Offer',
        url: offer.url,
        price: offer.price,
        priceCurrency: offer.priceCurrency,
        availability: offer.availability,
        validFrom: offer.validFrom,
      }),
    ),
  });
}

export interface TouristAttractionSchemaInput {
  name: string;
  description?: string;
  imageUrl?: string;
  url: string;
  businessInfo?: BusinessInfo | null;
}

export function buildTouristAttractionSchema({
  name,
  description,
  imageUrl,
  url,
  businessInfo,
}: TouristAttractionSchemaInput): JsonLd {
  const address = businessInfo
    ? pruneEmpty({
        '@type': 'PostalAddress',
        streetAddress: businessInfo.streetAddress,
        addressLocality: businessInfo.addressLocality,
        addressRegion: businessInfo.addressRegion,
        postalCode: businessInfo.postalCode,
        addressCountry: businessInfo.addressCountry,
      })
    : null;

  const geo =
    businessInfo?.latitude != null && businessInfo?.longitude != null
      ? {
          '@type': 'GeoCoordinates',
          latitude: businessInfo.latitude,
          longitude: businessInfo.longitude,
        }
      : null;

  return pruneEmpty({
    '@context': 'https://schema.org',
    '@type': 'TouristAttraction',
    name,
    description,
    image: imageUrl,
    url,
    address: address && Object.keys(address).length > 1 ? address : undefined,
    geo,
  });
}
